import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { inject, isDevMode, provideAppInitializer } from '@angular/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarConfig,
} from '@angular/material/snack-bar';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
  withRouterConfig,
} from '@angular/router';
import { appInitialiserFactory } from '@app/app-initialiser.factory';
import { AppComponent } from '@app/app.component';
import { DynamicWidgetPageComponent } from '@app/components/dynamic-widget-page.component';
import { DefaultConfigResolverGuard } from '@app/guards/default-config-resolver.guard';
import authCookieInterceptor from '@app/interceptors/auth-cookie.interceptor';
import { environment } from '@env/environment';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore, Store, withNgxsPlugin } from '@ngxs/store';
import { DispatcherService } from '@app/services/dispatcher.service';
import { NgxsWidgetPluginService } from '@app/services/ngxs-widget-plugin.service';
import { ACTION_DISPATCHER } from '@app/models/action-dispatcher.model';
import { WidgetsState } from '@app/state/widgets/widgets.state';
import '@app/widgets-registry';
import {
  DEFAULT_FALLBACK_LANGUAGE_CODE,
  TranslationState,
} from '@app/state/translations/translation.state';
import { LayoutState } from '@app/state/layout/layout.state';
import {
  APP_CONFIG,
  AppConfigModel,
  getApiUrl,
} from '@app/models/app-config.model';
import dateFnsLocales from '@app/models/date-fns-locales';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: APP_CONFIG,
      useValue: {
        ...environment,
        API_URL: getApiUrl(
          location.hostname,
          environment.APP_ID || 0,
          environment.BASE_HREF || '/'
        ),
      },
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: dateFnsLocales[DEFAULT_FALLBACK_LANGUAGE_CODE],
    },
    {
      provide: ACTION_DISPATCHER,
      useClass: DispatcherService,
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useFactory: (config: AppConfigModel) =>
        ({
          duration: config.DEFAULT_NOTIFICATION_DURATION,
        }) as MatSnackBarConfig,
      deps: [APP_CONFIG],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideAppInitializer(() => {
      const initializerFn = appInitialiserFactory(
        inject(Store),
        inject(DateAdapter)
      );
      return initializerFn();
    }),
    provideDateFnsAdapter(),
    provideHttpClient(withFetch(), withInterceptors([authCookieInterceptor])),
    provideRouter(
      [
        {
          path: '',
          redirectTo: 'login',
          pathMatch: 'full',
        },
        {
          path: '**',
          component: DynamicWidgetPageComponent,
          runGuardsAndResolvers: 'always',
          resolve: {
            widget: DefaultConfigResolverGuard,
          },
        },
      ],
      withHashLocation(),
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      })
    ),
    provideAnimations(),
    provideStore(
      [WidgetsState, TranslationState, LayoutState],
      {
        developmentMode: !isDevMode(),
        compatibility: {
          strictContentSecurityPolicy: true,
        },
        selectorOptions: {
          injectContainerState: true, // TODO: Convert all selectors and remove this
        },
      },
      withNgxsPlugin(NgxsWidgetPluginService),
      withNgxsStoragePlugin({
        keys: [
          'translation.languageCode',
          'layout.activeClientId',
          'menu',
          'filters.startDate',
          'filters.endDate',
          'widgets.outputs.filter_startDate.value',
          'widgets.outputs.filter_endDate.value',
        ],
      }),
      withNgxsRouterPlugin(),
      withNgxsReduxDevtoolsPlugin({
        disabled: !isDevMode(),
      }),
      withNgxsLoggerPlugin({
        disabled: !isDevMode(),
      })
    ),
  ],
}).catch(err => console.error(err));
